import type { RunResult, RunStatusType } from "./RulesResultPanel";

/**
 * True for statuses where the rule run has not concluded yet (work still in flight).
 * Kept as a separate predicate from `isExpandableResult`: a status can be terminal
 * (the run has concluded) without being expandable — see CANCELED below.
 */
export function isNonTerminal(status: RunStatusType): boolean {
  return status === "RUNNING" || status === "QUEUED";
}

/**
 * True for terminal statuses that carry a concluded pass/fail/error verdict and are
 * therefore worth expanding into the accordion detail view. CANCELED is terminal but
 * intentionally NOT expandable (there is no result content to show), so it must return
 * false here even though `isNonTerminal("CANCELED")` is also false. Never combine these
 * two predicates into a single `isTerminal()` — they answer different questions.
 */
export function isExpandableResult(status: RunStatusType): boolean {
  return status === "VERIFIED" || status === "FAILED" || status === "ERROR";
}

/**
 * Primary sort key for `sortResults`: severity/lifecycle order across all six statuses.
 * Lower values sort first. See spec `05-data-model.md` §5.2 / FR-009.
 */
export const STATUS_ORDER = { ERROR: 0, FAILED: 1, VERIFIED: 2, RUNNING: 3, QUEUED: 4, CANCELED: 5 } as const;

/**
 * Shared results comparator (FR-009): primary key is status severity (`STATUS_ORDER`),
 * secondary key is rule `title` A→Z, case-insensitive. Deterministic and stable — returns
 * exactly `0` for equal status+title so a stable sort preserves input order. Used by both
 * `RulesPanel` (fixed-then-sort finalize) and the live/FLIP ordering mode so there is a
 * single source of truth for ordering.
 */
export function sortResults(a: RunResult, b: RunResult): number {
  const statusDelta = STATUS_ORDER[a.info.status] - STATUS_ORDER[b.info.status];
  if (statusDelta !== 0) return statusDelta;
  return a.title.localeCompare(b.title, undefined, { sensitivity: "base" });
}
